<template>
  <div class="relative w-full bg-[#0f0f0f] mt-[60px] pt-[20px] lg:pt-[30px] pb-[80px] text-white">

    <!-- Color blobs -->
    <div class="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      <div
        class="absolute top-[5%] right-[5%] w-[350px] h-[350px] rounded-full bg-[#22d3ee] opacity-[0.06] blur-[120px]">
      </div>
      <div
        class="absolute top-[25%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#7c3aed] opacity-[0.08] blur-[130px]">
      </div>
      <div
        class="absolute top-[60%] right-[-4%] w-[380px] h-[380px] rounded-full bg-[#a855f7] opacity-[0.07] blur-[140px]">
      </div>
      <div
        class="absolute top-[75%] left-[20%] w-[300px] h-[300px] rounded-full bg-[#4f8ef5] opacity-[0.06] blur-[110px]">
      </div>
    </div>

    <div class="relative z-10 w-full flex flex-col items-center px-4">

      <!-- Header Banner -->
      <section class="w-full max-w-6xl mx-auto mb-8">
        <div class="relative w-full h-[100px] md:h-[120px] lg:h-[130px] overflow-hidden rounded-[20px] flex items-center bg-[#0d1225] border-x border-[#1e2535]">
          <img src="@/assets/revamp/promotion-hero-1.png" alt=""
            class="absolute inset-0 w-full h-full object-cover object-center pointer-events-none z-0"
            aria-hidden="true" />
          <div class="absolute inset-0 bg-[#060b18]/20 pointer-events-none z-[1]"></div>
          <div
            class="absolute inset-0 bg-gradient-to-r from-[#060b18]/85 via-[#060b18]/30 to-transparent pointer-events-none z-[1]">
          </div>
          <div class="relative z-[2] pl-8 md:pl-10">
            <h1 class="font-montserrat font-extrabold text-[36px] sm:text-[42px] md:text-[48px] leading-[1.1] tracking-[-0.96px] text-white uppercase m-0">{{ $t('Profile.Header') }}</h1>
            <p class="text-[13px] sm:text-[14px] md:text-[15px] text-gray-300 mt-1 font-medium">{{ $t('Profile.Subtext') }}</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <div class="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6">

        <!-- LEFT: User Info Card -->
        <div
          class="w-full lg:w-[27%] bg-[#13161e] border border-[#1e2535] rounded-2xl p-6 flex flex-col items-center gap-4 self-start">

          <!-- Spinner -->
          <div v-if="loading" class="flex justify-center items-center h-[300px] w-full">
            <div class="twist-spinner w-[60px] h-[60px]"></div>
          </div>

          <template v-else>
            <!-- Avatar -->
            <div
              class="w-24 h-24 rounded-full overflow-hidden border-2 border-[#22d3ee] shadow-[0_0_14px_#22d3ee66] flex-shrink-0">
              <img :src="profileImage" alt="Avatar" class="w-full h-full object-cover" />
            </div>

            <!-- Name + verified -->
            <div class="text-center">
              <h2 class="text-xl font-bold text-white leading-tight">{{ displayName || 'Guest' }}</h2>
              <span v-if="isVerified"
                class="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-full bg-[#22d3ee]/10 border border-[#22d3ee]/30 text-[#22d3ee] text-xs font-semibold">
                {{ $t('Profile.Stake') }} Verified
                <img src="@/assets/verified.png" alt="Verified" class="w-4 h-4" />
              </span>
            </div>

            <!-- Email + Discord -->
            <div class="w-full text-center text-sm text-gray-400 space-y-2">
              <p><span class="text-gray-500">{{ $t('Common.DiscordUsername') }}</span> <span class="text-white">{{ discordUsername || '—' }}</span>
              </p>
              <button v-if="!discordID" @click="redirectToDiscord"
                class="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] active:scale-95 transition-all text-white font-semibold text-xs shadow-md">
                <img src="@/assets/discord.png" alt="Discord" class="h-3.5 w-3.5 object-contain brightness-0 invert" />
                Link Discord Account
              </button>
            </div>


            <!-- Member Since -->
            <div class="text-center text-sm">
              <p class="text-gray-500">{{ $t('Profile.MemberSince') }}</p>
              <p class="text-white font-semibold">{{ memberSince ? formatDate(memberSince) : '—' }}</p>
            </div>

            <!-- Divider -->
            <div class="w-full border-t border-[#1e2535]"></div>

            <!-- Points -->
            <div class="text-center">
              <div class="flex items-center justify-center gap-2 text-2xl font-bold text-white">
                <span class="text-yellow-400">☆</span>
                {{ (points || 0).toLocaleString() }}
              </div>
              <p class="text-gray-500 text-sm mt-1">{{ $t('Profile.YourPoints') }}</p>
            </div>

          </template>
        </div>

        <!-- RIGHT: stacked cards -->
        <div class="flex-1 flex flex-col gap-6">

          <!-- Linked Casino Accounts -->
          <div class="bg-[#13161e] border border-[#1e2535] rounded-2xl p-6">
            <div class="mb-5">
              <h3 class="text-lg font-bold text-white">{{ $t('Profile.LinkedCasinoAccounts') }}</h3>
              <p class="text-gray-500 text-sm mt-0.5">{{ $t('Profile.LinkCasinoAccountsDesc') }}</p>
            </div>

            <!-- Platform cards -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">

              <!-- 21.com -->
              <div class="bg-[#0d0f14] border border-[#1e2535] rounded-xl p-4 flex flex-col gap-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <img src="@/assets/revamp/logo21-com.png" alt="21.com" class="h-3.5 object-contain" />
                    <span class="text-white font-semibold text-sm">21.com</span>
                  </div>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full border border-[#1e2535] bg-[#1e2535]/60 text-gray-400 font-medium">
                    {{ twentyOneUsername ? $t('Profile.Linked') : $t('Profile.Not_Linked') }}
                  </span>
                </div>
                <div>
                  <label class="text-gray-500 text-xs mb-1 block">{{ $t('Profile.Username') }}</label>
                  <input type="text" :value="twentyOneUsername || ''" readonly
                    class="w-full bg-[#13161e] border border-[#1e2535] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none cursor-default"
                    :placeholder="$t('Profile.Not_Linked')" />
                </div>
              </div>

              <!-- Discord -->
              <div class="bg-[#0d0f14] border border-[#1e2535] rounded-xl p-4 flex flex-col gap-3">
                <div>
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-2">
                      <img src="@/assets/discord.png" alt="Discord" class="h-4 w-4 object-contain" />
                      <span class="text-white font-semibold text-sm">Discord</span>
                    </div>
                    <span
                      class="text-xs px-2 py-0.5 rounded-full border border-[#1e2535] bg-[#1e2535]/60 font-medium"
                      :class="discordID ? 'text-green-400 border-green-500/30 bg-green-500/10' : 'text-gray-400'">
                      {{ discordID ? $t('Profile.Linked') : $t('Profile.Not_Linked') }}
                    </span>
                  </div>
                  <div>
                    <label class="text-gray-500 text-xs mb-1 block">{{ $t('Profile.Username') }}</label>
                    <input type="text" :value="discordUsername || ''" readonly
                      class="w-full bg-[#13161e] border border-[#1e2535] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none cursor-default"
                      :placeholder="$t('Profile.Not_Linked')" />
                  </div>
                </div>
                <button v-if="!discordID" @click="redirectToDiscord"
                  class="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-xs font-semibold active:scale-95 transition-all shadow-md mt-1 bg-[#5865F2] hover:bg-[#4752C4] text-white">
                  <img src="@/assets/discord.png" alt="Discord" class="h-3.5 w-3.5 object-contain brightness-0 invert" />
                  Link Discord Account
                </button>
              </div>

              <!-- Kick -->
              <div class="bg-[#0d0f14] border border-[#1e2535] rounded-xl p-4 flex flex-col gap-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <img src="@/assets/kick-logo.png" alt="Kick" class="h-4 w-4 object-contain" />
                    <span class="text-white font-semibold text-sm">Kick</span>
                  </div>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full border border-[#1e2535] bg-[#1e2535]/60 text-gray-400 font-medium">
                    {{ kick ? $t('Profile.Linked') : $t('Profile.Not_Linked') }}
                  </span>
                </div>
                <div>
                  <label class="text-gray-500 text-xs mb-1 block">Username</label>
                  <input type="text" :value="kick || ''" readonly
                    class="w-full bg-[#13161e] border border-[#1e2535] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none cursor-default"
                    placeholder="Not linked" />
                </div>
              </div>
            </div>


          </div>

          <!-- Important Disclaimer -->
          <div class="bg-[#13161e] border border-red-900/40 rounded-2xl p-6">
            <div class="flex items-center gap-2 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-red-500 flex-shrink-0" fill="none"
                viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h4 class="text-red-500 font-semibold text-base">{{ $t('Profile.DisclaimerNew.Title') }}</h4>
            </div>
            <div class="space-y-3 text-sm text-gray-300 leading-relaxed">
              <p><span class="text-red-500 font-semibold">{{ $t('Profile.DisclaimerNew.Point1_Title') }}</span> {{ $t('Profile.DisclaimerNew.Point1_Text') }}</p>
              <p><span class="text-red-500 font-semibold">{{ $t('Profile.DisclaimerNew.Point2_Title') }}</span> {{ $t('Profile.DisclaimerNew.Point2_Text') }}</p>
              <p><span class="text-red-500 font-semibold">{{ $t('Profile.DisclaimerNew.Point3_Title') }}</span> {{ $t('Profile.DisclaimerNew.Point3_Text') }}</p>
              <p><span class="text-red-500 font-semibold">{{ $t('Profile.DisclaimerNew.Point4_Title') }}</span> {{ $t('Profile.DisclaimerNew.Point4_Text') }}</p>
            </div>
          </div>

        </div>
      </div>

    </div>
  </div>


  <ProfileLockModal :showModal="showDaysLeftModal" :daysLeft="daysLeft" @close="closeProfileLockModal" />
</template>

<script>
import ProfileLockModal from "@/components/ProfileLockModal.vue";
import axios from "axios";

export default {
  name: "ProfilePage",
  components: {
    ProfileLockModal,
  },
  computed: {
    profileImage() {
      return this.$store.state.avatar || 'https://i.pravatar.cc/80?img=12';
    },
    isUserLoggedIn() {
      return !!this.$store.state.user;
    },
    isDiscordLinked() {
      return !!this.discordID;
    },
  },
  data() {
    return {
      isReady: false,
      displayName: null,
      picUrl: null,
      discordID: null,
      discordUsername: null,
      twitter: null,
      twentyOneUsername: null,
      kick: null,
      email: null,
      points: 0,
      memberSince: null,
      userRole: null,
      showModal: false,
      showDaysLeftModal: false,
      loading: false,
      daysLeft: 0,
      isVerified: false,
    };
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-CA');
    },
    init() {
      const token = localStorage.getItem("maherco-access-token");

      if (token) {
        this.loading = true;
        this.$store.commit("UPDATE_USER", { accessToken: token });

        axios
          .get(process.env.VUE_APP_BASE_API + "/api/users/me", {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            if (res.data) {
              this.displayName = res.data.user.username;
              this.picUrl = res.data.user.profileImage;
              this.$store.commit("UPDATE_AVATAR", { avatar: this.picUrl });
              this.discordID = res.data.user.discordId;
              this.discordUsername = res.data.user.discordUsername;
              this.twitter = res.data.user.twitter;
              this.twentyOneUsername = res.data.user.twentyOneUsername;
              this.kick = res.data.user.kick;
              this.isVerified = res.data.user.isVerified;
              this.email = res.data.user.email || null;
              this.points = res.data.user.points || 0;
              this.memberSince = res.data.user.createdAt || null;
              this.userRole = res.data.user.role || null;

              const lastUpdated = new Date(res.data.user.updatedAt);
              const currentDate = new Date();
              this.daysLeft = 60 - Math.floor((currentDate - lastUpdated) / (1000 * 60 * 60 * 24));
              if (this.daysLeft < 0) this.daysLeft = 0;



              if (this.kick) {
                axios
                  .get(`https://kick.com/api/v2/channels/${this.kick}`)
                  .then((kickRes) => {
                    const kickAvatar = kickRes.data?.user?.profile_pic;
                    if (kickAvatar) {
                      this.$store.commit("UPDATE_AVATAR", { avatar: kickAvatar });
                    }
                  })
                  .catch(() => { });
              }
            }
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              localStorage.removeItem("vuex");
              localStorage.removeItem("maherco-access-token");
              this.$router.replace("/");
            } else {
              console.log(`Error: ${error.response?.status}`);
            }
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.$router.replace("/");
      }
    },
    checkEditPermission() {
      if (!this.twitter) {
        this.showModal = true;
        this.showDaysLeftModal = false;
      } else if (this.daysLeft > 0) {
        this.showDaysLeftModal = true;
        this.showModal = false;
      }
    },
    redirectToDiscord() {
      const token = localStorage.getItem("maherco-access-token");
      const baseUrl = process.env.VUE_APP_BASE_API;
      const loginUrl = token 
        ? `${baseUrl}/api/auth/link/discord?token=${encodeURIComponent(token)}`
        : `${baseUrl}/api/auth/link/discord`;
      window.location.href = loginUrl;
    },
    redirectToKick() {
      if (this.displayName) {
        window.location.href = `https://kick.com/${this.displayName}`;
      }
    },
    closeProfileLockModal() {
      this.showDaysLeftModal = false;
    },
    closeModal() {
      this.showModal = false;
    },
  },
  mounted() {
    this.init();

    const error = this.$route.query.error;
    if (error) {
      this.$router.replace({
        name: "ErrorView",
        query: { error },
      });
    }
  },
};
</script>

<style scoped>
.twist-spinner {
  width: 3rem;
  height: 3rem;
  border: 4px solid transparent;
  border-top: 4px solid #22d3ee;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

@keyframes twist-spin {
  0% {
    transform: rotate(0deg) scale(1);
  }

  50% {
    transform: rotate(180deg) scale(1.1);
  }

  100% {
    transform: rotate(360deg) scale(1);
  }
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}
</style>
